<template>
  <div v-if="hasData">
    <div class="cm-chart-title">
      <div class="cm-seg">
        <button
          v-for="m in metrics"
          :key="m.key"
          :class="{ active: metric === m.key }"
          @click="setMetric(m.key)"
        >{{ $t(m.tabLabel) }}</button>
      </div>
      <div class="period-btns" v-click-outside="closeGranularity">
        <span
          class="cm-select-label"
          :class="{ active: granularityOpen }"
          @click="granularityOpen = !granularityOpen"
        >
          {{ $t(currentGranularityLabel) }}
          <span class="expand"></span>
        </span>
        <ul v-show="granularityOpen">
          <li
            v-for="g in granularities"
            :key="g.key"
            :class="{ active: granularity === g.key }"
            @click="setGranularity(g.key)"
          >
            {{ $t(g.label) }}
          </li>
        </ul>
      </div>
      <button
        class="cm-view-toggle"
        :class="{ active: view === 'heatmap' }"
        :title="$t(view === 'heatmap' ? 'CM_TL_VIEW_LINE' : 'CM_TL_VIEW_HEATMAP')"
        @click="toggleView"
      >
        <i :class="view === 'heatmap' ? 'bi bi-graph-up' : 'bi bi-grid-3x3-gap'"></i>
      </button>
    </div>

    <!-- относит. TR по страницам без комментарийного фона не считается (NULL) —
         на этой вкладке объясняем, а не показываем пустой график -->
    <p v-if="showTrNotice" class="cm-hint">{{ $t('CM_TR_NO_SIGNAL') }}</p>
    <div v-else class="m-chart">
      <div ref="main" class="chart"></div>
    </div>

  </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce, merge } from "lodash";
import moment from "moment";
import comOpt from "@/models/charts/common-options.json";
import { enableTouchTooltip } from "@/models/charts/echarts-touch";
import { mapGetters } from "vuex";
import ClickOutside from "vue-click-outside";
import {
  aggregateSeries,
  bucketsBetween,
  defaultGranularity,
} from "@/api/controllers/content-monitoring";

const METRICS = [
  { key: "mentions", tabLabel: "CM_TL_TAB_MENTIONS", label: "CM_TL_MENTIONS", colorKey: "red" },
  { key: "rel_er", tabLabel: "CM_TL_TAB_ER", label: "CM_TL_ER", colorKey: "green" },
  { key: "rel_tr", tabLabel: "CM_TL_TAB_TR", label: "CM_TL_TR", colorKey: "blue" },
];

export default {
  name: "Cm-term-timeline",
  directives: { ClickOutside },

  props: {
    // дневной ряд ОДНОГО терма из /term: [{date, mentions, rel_er, rel_tr}].
    // Разрежённые дни (без упоминаний) сервер не присылает — ось достраиваем
    // по периоду сами (см. points), иначе пропуски исчезают с графика.
    series: { type: Array, default: () => [] },
  },

  data() {
    return {
      metric: "mentions",
      view: "line", // line | heatmap
      granularity: "day",
      granularityOpen: false,
      metrics: METRICS,
      granularities: [
        { key: "day", label: "DASH_DAILY" },
        { key: "week", label: "DASH_WEEKLY" },
        { key: "month", label: "DASH_MONTHLY" },
      ],
    };
  },

  computed: {
    /** Терм мог не встретиться ни разу — пустые графики не рисуем. */
    hasData() {
      return this.series.some((p) => (p.mentions || 0) > 0);
    },

    /** Обсуждаемость есть, если в ряду встречается хоть одно не-NULL значение. */
    hasTrData() {
      return this.series.some((p) => p.rel_tr != null);
    },

    /** Выбрана вкладка TR, а считать её не по чему. */
    showTrNotice() {
      return this.metric === "rel_tr" && !this.hasTrData;
    },

    activeMetric() {
      return this.metrics.find((m) => m.key === this.metric) || this.metrics[0];
    },

    currentGranularityLabel() {
      const found = this.granularities.find((g) => g.key === this.granularity);
      return found ? found.label : "";
    },

    metricColor() {
      return this.$colors[this.activeMetric.colorKey] || this.$colors.red;
    },

    metricTitle() {
      return this.$t(this.activeMetric.label);
    },

    ...mapGetters(["getPeriod"]),

    periodKey() {
      const period = this.getPeriod || {};
      return `${period.start}..${period.end}`;
    },

    /**
     * Ряд под выбранную гранулярность на ВСЕХ корзинах периода. В корзине без
     * упоминаний их ноль (линия падает к нулю), а относительных метрик нет —
     * там null: ось только из дат с данными прятала пропуски, и слово с парой
     * упоминаний за год выглядело ровной линией.
     */
    points() {
      const period = this.getPeriod || {};
      const byDate = new Map(
        aggregateSeries(this.series, this.granularity).map((p) => [p.date, p]),
      );
      const dates = new Set(bucketsBetween(period.start, period.end, this.granularity));
      byDate.forEach((_, date) => dates.add(date));
      return [...dates].sort().map((date) =>
        byDate.get(date) || { date, mentions: 0, rel_er: null, rel_tr: null },
      );
    },

    /** Подписи оси X: месяц → «MMM YYYY», иначе «DD MMM» (как в Cm-timelines). */
    labels() {
      const fmt = this.granularity === "month" ? "MMM YYYY" : "DD MMM";
      return this.points.map((p) => moment(p.date).format(fmt));
    },
  },

  watch: {
    // период сменился — грануляцию выбираем заново (ручной выбор живёт до этого)
    periodKey: {
      immediate: true,
      handler() {
        const period = this.getPeriod || {};
        this.granularity = defaultGranularity(period.start, period.end);
      },
    },
    // панель пересоздаётся на смену терма (loading прячет блок), но props тоже страхуем
    series() {
      this.$nextTick(this.renderChart);
    },
  },

  methods: {
    setMetric(key) {
      if (this.metric === key) return;
      const wasNotice = this.showTrNotice;
      this.metric = key;
      // контейнер графика подменяется пояснением (и обратно) — старый инстанс
      // остался бы привязан к выброшенному узлу, поэтому пересоздаём
      if (wasNotice !== this.showTrNotice) this.disposeChart();
      this.$nextTick(this.renderChart);
    },

    closeGranularity() {
      this.granularityOpen = false;
    },

    setGranularity(key) {
      this.granularityOpen = false;
      if (this.granularity === key) return;
      this.granularity = key;
      this.$nextTick(this.renderChart);
    },

    toggleView() {
      this.view = this.view === "line" ? "heatmap" : "line";
      // тип series меняется целиком — пересоздаём инстанс
      this.disposeChart();
      this.$nextTick(this.renderChart);
    },

    /** Тултип линии: прозрачная оболочка + .tooltip-list. */
    lineTooltipFormatter(params) {
      if (!params.length) return "";
      const p = params[0];
      if (p.data == null) return "";
      const color = this.metricColor;
      return `<div class="tooltip">
        <span class="date">${p.axisValueLabel || p.name}</span>
        <ul class="tooltip-list">
          <li data-value-lines="${p.data}">
            <div class="soc-sea" style="color:${color}">
              <span class="cm-dot" style="background:${color};margin-right:6px"></span>${p.seriesName}
            </div>${Number(p.data).toLocaleString("ru-RU")}
          </li>
        </ul>
      </div>`;
    },

    heatTooltipFormatter(params) {
      const value = Array.isArray(params.value) ? params.value[1] : params.value;
      if (value == null) return "";
      const color = this.metricColor;
      const date = Array.isArray(params.value) ? params.value[0] : params.name;
      const label = moment(date).format(
        this.granularity === "month" ? "MMM YYYY" : "DD MMM YYYY",
      );
      return `<div class="tooltip">
        <span class="date">${label}</span>
        <ul class="tooltip-list">
          <li data-value="${value}">
            <div class="soc-sea" style="color:${color}">
              <span class="cm-dot" style="background:${color};margin-right:6px"></span>${this.metricTitle}
            </div>${Number(value).toLocaleString("ru-RU")}
          </li>
        </ul>
      </div>`;
    },

    lineOption() {
      const color = this.metricColor;
      const rgb = echarts.color.parse(color).slice(0, 3).join(",");
      const key = this.metric;
      return merge({}, comOpt, {
        grid: { top: 10, right: 8, bottom: 4, left: 4, containLabel: true },
        tooltip: {
          trigger: "axis",
          triggerOn: "mousemove|click",
          confine: true,
          axisPointer: {
            show: true,
            snap: true,
            label: { show: false },
            lineStyle: { color: "#C2D5FF", type: "line" },
          },
          formatter: this.lineTooltipFormatter,
        },
        xAxis: { type: "category", boundaryGap: false, data: this.labels },
        yAxis: { type: "value" },
        series: [
          {
            name: this.metricTitle,
            type: "line",
            smooth: 0.4,
            // у относительных метрик в пропуске значения нет — разрыв, а
            // одиночные точки показываем символом; упоминания в пропуске — ноль
            connectNulls: false,
            showSymbol: key !== "mentions",
            symbolSize: key === "mentions" ? 6 : 4,
            lineStyle: { width: 2, color },
            itemStyle: { color },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `rgba(${rgb}, 0.35)` },
                { offset: 1, color: `rgba(${rgb}, 0.02)` },
              ]),
            },
            emphasis: {
              focus: "series",
              symbol: "circle",
              itemStyle: {
                color,
                borderColor: `rgba(${rgb}, 0.3)`,
                borderWidth: 10,
                opacity: 1,
              },
            },
            data: this.points.map((p) => (p[key] == null ? null : p[key])),
          },
        ],
      });
    },

    /**
     * Календарная heatmap по выбранной метрике (как audience online heatmap:
     * цвет от светлого к metricColor).
     */
    heatmapOption() {
      const color = this.metricColor;
      const key = this.metric;
      const heatData = this.points
        .filter((p) => p[key] != null)
        .map((p) => [p.date, p[key]]);
      if (!heatData.length) return null;

      const values = heatData.map((d) => d[1]);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      const minColor = echarts.color.lift(color, 0.85);
      const start = heatData[0][0];
      const end = heatData[heatData.length - 1][0];

      return {
        tooltip: {
          position: "top",
          triggerOn: "mousemove|click",
          confine: true,
          backgroundColor: "transparent",
          borderWidth: 0,
          padding: 0,
          transitionDuration: 0.4,
          formatter: this.heatTooltipFormatter,
        },
        visualMap: {
          min: minValue,
          max: maxValue === minValue ? minValue + 1 : maxValue,
          calculable: false,
          orient: "horizontal",
          left: "center",
          bottom: 0,
          itemWidth: 12,
          itemHeight: 4,
          show: false,
          inRange: { color: [minColor, color] },
        },
        calendar: {
          top: 24,
          left: 36,
          right: 8,
          bottom: 16,
          cellSize: ["auto", 16],
          range: [start, end],
          itemStyle: {
            borderWidth: 2,
            borderColor: "#fff",
            borderRadius: 3,
          },
          splitLine: { show: false },
          yearLabel: { show: false },
          monthLabel: {
            fontSize: 10,
            fontFamily: "Lato, Helvetica, Arial, sans-serif",
            color: "#000",
          },
          dayLabel: {
            firstDay: 1,
            fontSize: 9,
            fontFamily: "Lato, Helvetica, Arial, sans-serif",
            color: "#000",
            nameMap: "EN",
          },
        },
        series: [
          {
            type: "heatmap",
            coordinateSystem: "calendar",
            data: heatData,
            label: { show: false },
            itemStyle: {
              borderRadius: 3,
              borderWidth: 1,
              borderColor: "#fff",
            },
          },
        ],
      };
    },

    renderChart() {
      if (!this.hasData) return;
      const el = this.$refs.main;
      if (!el) return;

      el.style.touchAction = "manipulation";
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(el, null, { renderer: "canvas" });
        enableTouchTooltip(this.chartInstance);
      }

      const option =
        this.view === "heatmap" ? this.heatmapOption() : this.lineOption();
      if (!option) return;

      this.chartInstance.setOption(option, { notMerge: true });
      this.chartInstance.resize();
    },

    disposeChart() {
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }
    },

    resizeChart() {
      if (this.chartInstance) this.chartInstance.resize();
    },
  },

  created() {
    // инстанс echarts держим ВНЕ data — реактивность ему не нужна
    this.chartInstance = null;
    this.onResize = debounce(this.resizeChart, 150);
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.$nextTick(this.renderChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.onResize.cancel();
    this.disposeChart();
  },
};
</script>
